<template>
  <div v-if="loading" class="history-timeline__loading text-color-secondary">Loading timeline…</div>
  <div v-else-if="!groups.length" class="p-4 text-center text-color-secondary">No history entries</div>
  <div v-else class="history-timeline">
    <article v-for="group in groups" :key="`${group.refType}:${group.refId}`" class="history-timeline__card">
      <header class="history-timeline__head">
        <div class="history-timeline__title">
          <strong>{{ group.refName || '—' }}</strong>
          <Tag :value="typeLabel(group.refType)" :severity="typeSeverity(group.refType)" />
          <span class="history-mono text-sm text-color-secondary">{{ group.refId }}</span>
        </div>
        <div class="history-timeline__meta text-sm text-color-secondary">
          {{ group.count }} change{{ group.count === 1 ? '' : 's' }}
          · latest {{ formatWhen(group.latestTimestamp) }}
        </div>
      </header>
      <div class="lead-history-list">
        <div
          v-for="item in group.events || []"
          :key="item.historyId || `${item.timestamp}-${item.field}`"
          class="lead-history-item"
        >
          <div class="lead-history-item__when">{{ formatWhen(item.timestamp) }}</div>
          <div class="lead-history-item__what">
            <strong>{{ item.field || '—' }}</strong>
            · {{ displayValue(item.newValue) }}
            <span v-if="item.oldValue" class="lead-history-item__who">
              (was: {{ truncate(item.oldValue, 50) }})
            </span>
          </div>
          <div class="lead-history-item__who">
            {{ item.changedBy || '—' }}
            <template v-if="item.note"> · {{ item.note }}</template>
          </div>
        </div>
      </div>
    </article>
  </div>
</template>

<script setup>
import Tag from 'primevue/tag';
import { formatDateTime } from '@/utils/common';
import '../potential/lead-profile.css';

defineOptions({ name: 'HistoryTimeline' });

defineProps({
  groups: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false }
});

function formatWhen(value) {
  return formatDateTime(value) || value || '—';
}

function typeLabel(type) {
  const map = {
    lead: 'Lead',
    inprogress: 'In Progress',
    driver: 'Driver'
  };
  return map[type] || type || '—';
}

function typeSeverity(type) {
  if (type === 'lead') return 'info';
  if (type === 'inprogress') return 'warn';
  if (type === 'driver') return 'success';
  return 'secondary';
}

function displayValue(value) {
  if (value == null || value === '') return '(empty)';
  return String(value);
}

function truncate(value, max) {
  const text = String(value ?? '');
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}
</script>

<style scoped>
.history-timeline {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}
.history-timeline__card {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 0.75rem;
  padding: 0.9rem 1rem;
}
.history-timeline__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
}
.history-timeline__title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
}
.history-mono {
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}
.history-timeline__loading {
  padding: 1rem;
  text-align: center;
}
</style>
